/**
 * Workspace artifact store slice — Phase 2.1 of the Workspace Artifact
 * Contract. The frontend keeps the canonical list of artifacts emitted during
 * a Deep Explorer (Workspace) turn so renderers can subscribe to a single
 * source of truth.
 *
 * Spec: `.workspace-spec/WORKSPACE_CONTRACT.md`
 *
 * The slice owns a top-level `artifactsByConversation` map keyed by session
 * id. The contract calls the key `conversationId` (it matches each artifact's
 * `conversation_id` field on the wire) but the frontend exclusively addresses
 * sessions by `sessionId`, so the public selectors take `sessionId`.
 *
 * Append-only with version-tree semantics:
 *   - Artifacts arrive via `artifact_emit` and `artifact_update` WS frames.
 *   - `appendArtifact` flips the parent's `is_latest` to `false` when a child
 *     lands so `getLatestPerLineage` always reflects the freshest version.
 *   - `applyArtifactPatch` shallow-merges into the top-level fields and (if
 *     present) the `payload` object — no deep merge.
 *   - `hydrateArtifactsFromContext` replaces the session's list wholesale and
 *     is used on chat reload from `context.artifacts`.
 */
import type { Artifact } from "@/api/models"
import type { StateCreator } from "zustand"

import { logger } from "@/lib/logger"

type ArtifactsSliceCreator = (
  set: Parameters<StateCreator<ArtifactsSlice>>[0],
  get: Parameters<StateCreator<ArtifactsSlice>>[1]
) => ArtifactsSlice

// ── State shape ──────────────────────────────────────────────────────────────

export interface ArtifactsSlice {
  /** Top-level map of session id → ordered list of artifacts (emit order). */
  artifactsByConversation: Record<string, Artifact[]>

  /**
   * Per-session "active artifact id" used by Phase 4 (workspace-focus) so the
   * activity-lane → workspace correlation has a single source of truth.
   *
   * `null` (or an absent key) means "no explicit focus" — the workspace
   * falls back to its newest-first default. Per-session storage means
   * focusing artifact A on session 1 doesn't bleed into session 2 when the
   * user switches conversations and back.
   */
  activeArtifactIdByConversation: Record<string, string | null>

  /** Append a brand-new artifact and flip the parent's `is_latest` to false. */
  appendArtifact: (sessionId: string, artifact: Artifact) => void

  /**
   * Shallow-merge a patch into an existing artifact. If `patch.payload` is
   * present it is shallow-merged into the existing payload (preserving
   * unmentioned payload fields). Top-level fields are merged similarly.
   * No-op + warning when the id is unknown.
   */
  applyArtifactPatch: (sessionId: string, artifactId: string, patch: Partial<Artifact>) => void

  /**
   * Replace the session's artifact list with a persisted set (used on chat
   * reload from `context.artifacts`).
   */
  hydrateArtifactsFromContext: (sessionId: string, artifacts: Artifact[]) => void

  /**
   * Set (or clear) the active artifact id for a session. Idempotent — calling
   * with the same value as the current focus is a no-op so subscribers don't
   * thrash. Pass `null` to clear focus and let the workspace fall back to its
   * newest-first default.
   */
  focusArtifact: (sessionId: string, artifactId: string | null) => void
}

// ── Selector caches ──────────────────────────────────────────────────────────
//
// Selectors that derive new arrays must return stable references when the
// underlying input array hasn't changed — otherwise `useMemo` and React's
// reconciliation break. We cache the derived array against the input array
// with a WeakMap so repeated reads on unchanged state hit the cache.

const latestPerLineageCache = new WeakMap<Artifact[], Artifact[]>()
const versionChainCache = new WeakMap<Artifact[], Map<string, Artifact[]>>()

// ── Selectors ────────────────────────────────────────────────────────────────

interface ArtifactsReadableState {
  artifactsByConversation: Record<string, Artifact[]>
  activeArtifactIdByConversation?: Record<string, string | null>
}

const EMPTY_ARTIFACTS: Artifact[] = Object.freeze([]) as unknown as Artifact[]

/** All artifacts for `sessionId` in emit order. Returns a stable empty array
 *  when the session has no artifacts so consumers can rely on referential
 *  equality across renders. */
export function getArtifactsForSession(
  state: ArtifactsReadableState,
  sessionId: string | null | undefined
): Artifact[] {
  if (!sessionId) return EMPTY_ARTIFACTS

  return state.artifactsByConversation[sessionId] ?? EMPTY_ARTIFACTS
}

/** Latest `plan` artifact across all lineages in the session, or null when
 *  the session has no plan artifact yet. Used by PlanRenderer to render
 *  the long-form brief (Understanding / Expected result / Sources to consult)
 *  alongside the proposed step list — these fields live on the artifact
 *  payload but earlier renders showed only the steps, which lost the
 *  context the user needs to catch misreads before approving. */
export function getLatestPlanArtifact(
  state: ArtifactsReadableState,
  sessionId: string | null | undefined
): Artifact | null {
  const list = getArtifactsForSession(state, sessionId)
  if (list.length === 0) return null
  // Pick by max `version` rather than `is_latest` flag + arrival order.
  // Out-of-order WS arrivals can leave a stale v1 still flagged
  // `is_latest=true` if v2 arrived first; walking newest-first would then
  // return v1 (the most recently *arrived* plan that still claims latest).
  // Compare versions; break ties on `created_at` (more recent wins) so a
  // legitimate v1+v2 with identical version numbers (shouldn't happen but
  // would otherwise be ambiguous) still gets a deterministic answer.
  let best: Artifact | null = null
  for (const a of list) {
    if (a.type !== "plan") continue
    if (best === null) {
      best = a
      continue
    }
    if (a.version > best.version) {
      best = a
    } else if (a.version === best.version && a.created_at > best.created_at) {
      best = a
    }
  }
  return best
}

/** Latest version per lineage — relies on `is_latest` being maintained by
 *  `appendArtifact`. Stable across calls when the session list is unchanged. */
export function getLatestPerLineage(
  state: ArtifactsReadableState,
  sessionId: string | null | undefined
): Artifact[] {
  const list = getArtifactsForSession(state, sessionId)
  if (list.length === 0) return EMPTY_ARTIFACTS
  const cached = latestPerLineageCache.get(list)
  if (cached) return cached
  const next = list.filter((a) => a.is_latest)
  latestPerLineageCache.set(list, next)
  return next
}

/** Return the full lineage (root → newest) for the artifact's parent chain.
 *  Walks `parent_id` back to the root, then follows the children forward so
 *  v1 → v2 → … is complete regardless of which version `artifactId` points
 *  at. Defends against accidental cycles in either direction by tracking
 *  visited ids. Returns an empty array when the artifact isn't found. */
export function getVersionChain(
  state: ArtifactsReadableState,
  sessionId: string | null | undefined,
  artifactId: string
): Artifact[] {
  const list = getArtifactsForSession(state, sessionId)
  if (list.length === 0) return EMPTY_ARTIFACTS

  let perList = versionChainCache.get(list)
  if (!perList) {
    perList = new Map<string, Artifact[]>()
    versionChainCache.set(list, perList)
  }
  const cached = perList.get(artifactId)
  if (cached) return cached

  const byId = new Map<string, Artifact>()
  for (const a of list) byId.set(a.id, a)

  const start = byId.get(artifactId)
  if (!start) {
    perList.set(artifactId, EMPTY_ARTIFACTS)
    return EMPTY_ARTIFACTS
  }

  // Backward pass — walk parents to the root.
  const visited = new Set<string>()
  let root: Artifact = start
  let cursor: Artifact | undefined = start
  while (cursor) {
    if (visited.has(cursor.id)) {
      logger.warn(
        `getVersionChain: cycle detected at artifact id=${cursor.id}; returning empty chain`
      )
      perList.set(artifactId, EMPTY_ARTIFACTS)
      return EMPTY_ARTIFACTS
    }
    visited.add(cursor.id)
    root = cursor
    if (!cursor.parent_id) break
    cursor = byId.get(cursor.parent_id)
  }

  // Forward pass — build a parent → children index from the session list,
  // then walk descendants from the root. A version tree should be linear,
  // but if multiple children share a parent we follow the highest-version
  // child so the chain stays single-threaded.
  const childrenByParent = new Map<string, Artifact[]>()
  for (const a of list) {
    if (!a.parent_id) continue
    const bucket = childrenByParent.get(a.parent_id)
    if (bucket) bucket.push(a)
    else childrenByParent.set(a.parent_id, [a])
  }

  const chain: Artifact[] = [root]
  const forwardVisited = new Set<string>([root.id])
  let head = root
  for (;;) {
    const children = childrenByParent.get(head.id)
    if (!children || children.length === 0) break
    const next = children.reduce((best, candidate) =>
      candidate.version > best.version ? candidate : best
    )
    if (forwardVisited.has(next.id)) {
      logger.warn(
        `getVersionChain: forward cycle detected at artifact id=${next.id}; truncating chain`
      )
      break
    }
    forwardVisited.add(next.id)
    chain.push(next)
    head = next
  }

  perList.set(artifactId, chain)
  return chain
}

export function getArtifactById(
  state: ArtifactsReadableState,
  sessionId: string | null | undefined,
  artifactId: string
): Artifact | undefined {
  const list = getArtifactsForSession(state, sessionId)
  return list.find((a) => a.id === artifactId)
}

/**
 * Phase 4 (workspace-focus): the per-session active artifact id used to
 * route lane-entry clicks → workspace chip changes. Returns `null` when
 * unset so callers can cleanly fall back to a newest-first default.
 */
export function getActiveArtifactId(
  state: ArtifactsReadableState,
  sessionId: string | null | undefined
): string | null {
  if (!sessionId) return null
  const map = state.activeArtifactIdByConversation
  if (!map) return null
  if (!Object.prototype.hasOwnProperty.call(map, sessionId)) return null
  return map[sessionId] ?? null
}

// ── Internal helpers ─────────────────────────────────────────────────────────

function mergeArtifactPatch(prev: Artifact, patch: Partial<Artifact>): Artifact {
  const { payload: patchPayload, ...topLevel } = patch
  const next: Artifact = { ...prev, ...topLevel }
  if (patchPayload !== undefined) {
    // Shallow merge into the existing payload so callers can patch a single
    // field (e.g. `body`, `status`) without having to re-send the full payload.
    next.payload = {
      ...prev.payload,
      ...patchPayload,
    } as Artifact["payload"]
  }
  return next
}

// ── Slice factory ────────────────────────────────────────────────────────────

export const createArtifactsSlice: ArtifactsSliceCreator = (set, get) => ({
  artifactsByConversation: {},
  activeArtifactIdByConversation: {},

  appendArtifact(sessionId, artifact) {
    const map = get().artifactsByConversation

    // Producer hint → lineage link: if the artifact carries
    // `refines_artifact_id` and no explicit `parent_id`, treat the refines
    // pointer as the parent id. This way producers that only know "I refine
    // X" still get hooked into the lineage tree without having to track
    // versioning themselves. See WORKSPACE_CONTRACT.md § "Versioning rules".
    if (!artifact.parent_id && artifact.refines_artifact_id) {
      artifact = { ...artifact, parent_id: artifact.refines_artifact_id }
    }

    const prevList = map[sessionId] ?? EMPTY_ARTIFACTS

    // Id-dedup — the WS reattach replays its whole buffered window whenever a
    // subscriber attaches without `last_seen_seq` (e.g. a full page reload,
    // which resets the `lastSeenSeqRef` used to resume the stream, or a
    // second tab attaching fresh). Without this guard a replayed
    // `artifact_emit` for an id the store already has would be pushed again,
    // leaving two entries with the same id and, if both are `is_latest`, two
    // "latest" copies of the same lineage in `getLatestPerLineage`. Mirrors
    // the id-dedup `hydrateArtifactsFromContext` already does for the poll
    // path — replace the existing entry in place instead of appending.
    const existingIdx = prevList.findIndex((a) => a.id === artifact.id)
    let nextList: Artifact[]
    let newIdx: number
    if (existingIdx !== -1) {
      nextList = prevList.slice()
      nextList[existingIdx] = artifact
      newIdx = existingIdx
      if (artifact.parent_id) {
        const parentIdx = nextList.findIndex((a) => a.id === artifact.parent_id)
        if (parentIdx !== -1 && nextList[parentIdx].is_latest) {
          nextList[parentIdx] = { ...nextList[parentIdx], is_latest: false }
        }
      }
    } else if (artifact.parent_id) {
      const parentIdx = prevList.findIndex((a) => a.id === artifact.parent_id)
      if (parentIdx === -1) {
        logger.warn(
          `appendArtifact: parent id=${artifact.parent_id} not found for child id=${artifact.id} in session=${sessionId}; appending without parent flip`
        )
        nextList = [...prevList, artifact]
      } else {
        nextList = prevList.slice()
        nextList[parentIdx] = { ...nextList[parentIdx], is_latest: false }
        nextList.push(artifact)
      }
      newIdx = nextList.length - 1
    } else {
      nextList = [...prevList, artifact]
      newIdx = nextList.length - 1
    }

    // Out-of-order arrival reconciliation — frames can arrive child-before-parent
    // when a single WS batch is reordered. If any earlier-arrived artifact already
    // names the new one as its parent, the new artifact has been superseded —
    // flip it to is_latest=false. After this, every lineage has at most one
    // is_latest=true survivor regardless of arrival order. (#SP1)
    if (artifact.is_latest) {
      const supersededByExistingChild = nextList.some(
        (other, i) => i !== newIdx && other.parent_id === artifact.id
      )
      if (supersededByExistingChild) {
        nextList[newIdx] = { ...nextList[newIdx], is_latest: false }
      }
    }

    // Auto-advance the active artifact when:
    //   1) the new artifact refines the currently-active one (parent chain), OR
    //   2) a new `plan` arrives while the user is parked on a non-plan
    //      artifact (the ask_user form they just answered, or nothing yet).
    //
    // Case 2 fixes Erik's "no plan shown" symptom: after answering ask_user,
    // the workspace stayed pinned on the form because the plan artifact lives
    // in a different lineage (no parent_id link). The plan IS the surface the
    // user needs to approve, so it should always pull focus unless the user
    // is actively iterating on another plan version. Selections the user has
    // explicitly made to a non-latest version of a plan lineage are preserved
    // by case 1's same-lineage check.
    const activeMap = get().activeArtifactIdByConversation
    const currentActiveId = Object.prototype.hasOwnProperty.call(activeMap, sessionId)
      ? activeMap[sessionId]
      : null
    const currentActive = currentActiveId
      ? (nextList.find((a) => a.id === currentActiveId) ?? null)
      : null
    const refinesActive =
      Boolean(artifact.parent_id) &&
      Boolean(currentActiveId) &&
      artifact.parent_id === currentActiveId
    const planTakesFocus =
      artifact.type === "plan" && (!currentActive || currentActive.type !== "plan")
    // An incoming `ask_user` artifact blocks the run waiting for the user's
    // answer; if the workspace stays parked on a plan/doc the prompt UI is
    // hidden and the user can't unblock the agent (Erik's "it says it's
    // asking me a question, but nothing to do in the ui" symptom). Mirror
    // the `planTakesFocus` rule so an ask_user always steals focus unless
    // the user is already on another ask_user (no churn between question
    // versions). The artifact lives in its own lineage with no parent_id
    // link to the prior plan, so `refinesActive` never fires for it.
    const askUserTakesFocus =
      artifact.type === "ask_user" && (!currentActive || currentActive.type !== "ask_user")
    // An incoming deliverable (`document`) is the answer the user has been
    // waiting for — the entire turn was building toward it. Without this rule
    // the deliverable lands while the workspace is still parked on the plan,
    // the dropdown shows a "new artifact" dot but the user has to click it to
    // surface the answer (Erik feedback 2026-05-20). Mirror the
    // plan/ask_user rules: focus the new deliverable unless the user is
    // already parked on a deliverable (same lineage refinement is already
    // handled by `refinesActive`, which fires for v2/v3 of the same
    // deliverable lineage).
    const deliverableTakesFocus =
      artifact.type === "document" && (!currentActive || currentActive.type !== "document")
    const shouldAdvance =
      refinesActive || planTakesFocus || askUserTakesFocus || deliverableTakesFocus

    set({
      artifactsByConversation: {
        ...map,
        [sessionId]: nextList,
      },
      ...(shouldAdvance
        ? {
            activeArtifactIdByConversation: {
              ...activeMap,
              [sessionId]: artifact.id,
            },
          }
        : {}),
    })
  },

  applyArtifactPatch(sessionId, artifactId, patch) {
    const map = get().artifactsByConversation
    // The Record index type asserts non-nullable, but at runtime a missing
    // session id genuinely returns undefined. Treat both an absent key and an
    // empty list as "nothing to patch".
    const prevList: Artifact[] | undefined = Object.prototype.hasOwnProperty.call(map, sessionId)
      ? map[sessionId]
      : undefined
    if (!prevList) {
      logger.warn(
        `applyArtifactPatch: no artifacts for session=${sessionId} (id=${artifactId}); ignoring`
      )
      return
    }
    const idx = prevList.findIndex((a) => a.id === artifactId)
    if (idx === -1) {
      logger.warn(
        `applyArtifactPatch: artifact id=${artifactId} not found in session=${sessionId}; ignoring`
      )
      return
    }
    const nextList = prevList.slice()
    nextList[idx] = mergeArtifactPatch(prevList[idx], patch)
    set({
      artifactsByConversation: {
        ...map,
        [sessionId]: nextList,
      },
    })
  },

  hydrateArtifactsFromContext(sessionId, artifacts) {
    // MERGE, don't replace. The poll-based hydrator (`useDeepLivePoll`) calls
    // this every 2.5s with whatever's in Cosmos's `agent_run.live` snapshot.
    //
    // Three rules, in order:
    //   (a) Ids in prev but absent from incoming are KEPT. The original
    //       "plan disappears" bug came from wholesale replacement wiping
    //       freshly-arrived WS artifacts out of memory before Cosmos had
    //       flushed them. Never drop a known id on hydration. (Daniel,
    //       2026-05-12 — "plan rendered for a second then can't even
    //       select it from the dropdown".)
    //   (b) Ids in incoming and NOT in prev are appended. Standard
    //       cross-tab catch-up + dropped-WS recovery for new artifacts
    //       (e.g. deliverable emitted after a 1011 ping-timeout drop).
    //   (c) Ids present in BOTH: replace with incoming IF the payload
    //       actually differs. This is the post-incident addition (2026-05-12)
    //       so live-state ``artifact_update`` patches (like
    //       ``payload.todos`` flipping to completed) reach the FE when the
    //       WS missed the patch frame. Without it, plan todos showed as
    //       pending forever after a WS drop. The deep equality guard
    //       avoids churning the store on every poll tick when nothing
    //       changed.
    //
    // Worst-case staleness: between a WS update at T and the next Cosmos
    // flush (~5s), the polled snapshot can briefly lag. The next WS frame
    // (or next flush) reconciles. That ~2.5s flicker is the price for
    // permanent recovery after a dropped WS; we accept it.
    const map = get().artifactsByConversation
    const prevList = map[sessionId] ?? EMPTY_ARTIFACTS
    if (prevList.length === 0) {
      set({
        artifactsByConversation: {
          ...map,
          [sessionId]: [...artifacts],
        },
      })
      return
    }
    const incomingById = new Map(artifacts.map((a) => [a.id, a]))
    const prevById = new Map(prevList.map((a) => [a.id, a]))
    let changed = false
    const merged: Artifact[] = prevList.map((prev) => {
      const inc = incomingById.get(prev.id)
      if (!inc) return prev
      // Cheap deep-equal via JSON. Artifacts are small dicts (≤ a few KB);
      // running stringify per id per poll tick is negligible vs the
      // network round-trip we just paid to fetch them.
      if (JSON.stringify(prev) === JSON.stringify(inc)) return prev
      changed = true
      return inc
    })
    for (const incoming of artifacts) {
      if (!prevById.has(incoming.id)) {
        merged.push(incoming)
        changed = true
      }
    }
    if (!changed) return
    set({
      artifactsByConversation: {
        ...map,
        [sessionId]: merged,
      },
    })
  },

  focusArtifact(sessionId, artifactId) {
    if (!sessionId) return
    const map = get().activeArtifactIdByConversation
    const prev = Object.prototype.hasOwnProperty.call(map, sessionId)
      ? (map[sessionId] ?? null)
      : null
    if (prev === artifactId) return // idempotent — avoid pointless re-renders
    set({
      activeArtifactIdByConversation: {
        ...map,
        [sessionId]: artifactId,
      },
    })
  },
})
